import { SyncLoggingService } from '../../logging';
import { OfflineService, OfflineFirstDataService, DocumentRecord, ObserverService } from '../../offline-first';
import * as jiff from 'jiff';
import { difference, intersection, isArray, isObject, pickBy } from 'lodash';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { concatMap, map, tap } from 'rxjs/operators';
import { ExerciseProgramDocument, Program, ExerciseProgramDocumentCache } from '../models/exercise-program-document';
import { Plan, PlanDocument, PlanDocumentCache } from '../models/plan-document';
import { PlanMetadata } from '../models/plan-metadata';
import { DocumentRestService } from './document-rest.service';
import { UserLibraryDocument, UserLibrary, UserLibraryDocumentCache } from '../models/user-library-document';
import { WorkoutSessionDocument, WorkoutSession } from '../models/workout-session-document';
import { ObservationDocument, Observation } from '../models/observation-document';

const diffOptions = {
  invertible: false,
  hash: (x: any) => {
    if (x && x.id) {
      return x.id;
    } else {
      return isObject(x) ? JSON.stringify(pickBy(x, p => !isArray(p))) : isArray(x) ? JSON.stringify(x) : x;
    }
  },
};
export class DocumentSyncService extends OfflineFirstDataService {
  protected planUpdates: Subject<{ plan: PlanDocument; patch: object }> = new Subject<{ plan: PlanDocument; patch: object }>();

  constructor(protected documentRestService: DocumentRestService,
              offline: OfflineService,
              syncLog: SyncLoggingService,
              observer: ObserverService) {
    super(offline, syncLog, observer);
  }

  /**
   * Prepares to sync with the server.
   * Initializes progress counters.
   */
  protected prepareSync(): Observable<any> {
    this.syncLog.info('Preparing to sync documents with the server.');

    return this.offline.getAll<PlanMetadata>(PlanMetadata).pipe(
      tap((plans: PlanMetadata[]) => {
        this.syncsDone = 0;
        this.syncsTotal = 2; // add 2 for restoreData calls

        const plansToSync = plans.filter(plan => plan.isNew || plan.isUpdated);
        this.syncsTotal += plansToSync ? plansToSync.length : 0;

        const plansToDelete = plans.filter(plan => plan.isDeleted);
        this.syncsTotal += plansToDelete ? plansToDelete.length : 0;

        const plansToPull = plans.filter(plan => plan.isDownloaded);
        // remove deleted plans since they won't be pulled
        this.syncsTotal += (plansToPull ? plansToPull.length : 0) - (plansToDelete ? plansToDelete.length : 0);
      }),
    );
  }

  /**
   * Pushes any new plan documents, updates to plan documents, or deletes to the server
   * Pushes any updates to user library to the server
   */
  protected push(): Observable<any> {
    this.syncLog.info('Saving document changes to the server.');

    return forkJoin([this.pushPlans().pipe(concatMap(() => this.pushPlanMetadata())), this.pushUserLibary()]);
  }

  /**
   * Pushes new and updated plan documents to the server.
   * In the event of a version mismatch from the server, a "take mine" approach
   * is taken when pushing updates
   *
   *  @param plans optional observable yielding list of plans to push
   */
  private pushPlans(plans?: Observable<PlanDocument[]>) {
    return this.offline.getAll<PlanDocumentCache>(PlanDocumentCache).pipe(
      concatMap(cache => {
        return this.syncDirty<PlanDocument>(plans || PlanDocument, {
          model: PlanDocument,
          created: plan => this.documentRestService.createPlanDocument(plan),
          updated: plan => {
            return this.documentRestService.getPlanDocumentLatestVersion(plan.uuid).pipe(
              concatMap(latestVersion => {
                const cachedPlan = cache.find(p => p.uuid === plan.uuid);

                if (latestVersion !== plan.version) {
                  // if local version does not match remote, construct remote from patch stream and send
                  // diff with local as patch to server
                  return this.documentRestService.getPlanDocumentDiff(plan).pipe(
                    concatMap(res => {
                      const remotePlan = jiff.patch(res.patch, cachedPlan.document);
                      const patch = jiff.diff(remotePlan, plan.document, diffOptions);
                      if (patch && patch.length > 0) {
                        return this.documentRestService.patchPlanDocument(plan.uuid, patch).pipe(
                          concatMap(version => {
                            plan.version = version;
                            plan.document.version = version;
                            return this.offline.save(PlanDocument, plan);
                          }),
                        );
                      }
                      return of(null);
                    }),
                  );
                } else {
                  if (cachedPlan) {
                    const patch = jiff.diff(cachedPlan.document, plan.document, diffOptions);
                    if (patch && patch.length > 0) {
                      return this.documentRestService.patchPlanDocument(plan.uuid, patch).pipe(
                        concatMap(version => {
                          plan.version = version;
                          plan.document.version = version;
                          return this.offline.save(PlanDocument, plan);
                        }),
                      );
                    }
                  }
                  return of(null);
                }
              }),
            );
          },
          deleted: () => of(null),
        }).pipe(
          concatMap(syncedPlans => this.saveCache(syncedPlans)),
          tap(() => this.updateTotalProgress(1)),
        );
      }),
    );
  }

  /**
   * Pushes any deletes to the server.
   * @param plans optional observable yielding list of plans to push
   */
  private pushPlanMetadata(plans?: Observable<PlanMetadata[]>) {
    return this.syncDirty(plans || PlanMetadata, {
      model: PlanMetadata,
      created: () => of(null),
      updated: () => of(null),
      deleted: planMetadata => this.documentRestService.deletePlanDocument(planMetadata.uuid),
    });
  }

  /**
   * Pushes a plan to the server.
   * @param uuid plan uuid
   */
  protected pushPlan(uuid: string): Observable<void> {
    return this.offline.getRecord<PlanMetadata>(PlanMetadata, uuid).pipe(
      concatMap(planMetadata => {
        if (planMetadata.isDirty) {
          return this.pushPlans(this.offline.getRecord<PlanDocument>(PlanDocument, uuid).pipe(map(plan => [plan]))).pipe(
            concatMap(() => this.pushPlanMetadata(of([planMetadata]))),
            map(() => undefined)
          );
        } else {
          this.updateTotalProgress(1);
          return of(undefined);
        }
      }),
    );
  }

  /**
   * Pushes a user library document to the server
   * @param uuid user library document uuid
   */
  // TODO: make more generic
  protected pushUserLibary(): Observable<void> {
    return this.offline.getAll<UserLibraryDocumentCache>(UserLibraryDocumentCache).pipe(
      concatMap(cache => {
        return this.syncDirty<UserLibraryDocument>(UserLibraryDocument, {
          model: UserLibraryDocument,
          created: lib => this.documentRestService.createUserLibraryDocument(lib),
          updated: lib => {
            return this.documentRestService.getUserLibraryDocumentLatestVersion(lib.uuid).pipe(
              concatMap(latestVersion => {
                const cached = cache.find(c => c.uuid === lib.uuid);

                if (latestVersion !== lib.version) {
                  // if local version does not match remote, construct remote from patch stream and send
                  // diff with local as patch to server
                  return this.documentRestService.getUserLibraryDocumentDiff(lib).pipe(
                    concatMap(res => {
                      const remote = jiff.patch(res.patch, cached.document);
                      const patch = jiff.diff(remote, lib.document, diffOptions);
                      if (patch && patch.length > 0) {
                        return this.documentRestService.patchUserLibraryDocument(lib.uuid, patch).pipe(
                          concatMap(version => {
                            lib.version = version;
                            lib.document.version = version;
                            return this.offline.save(UserLibraryDocument, lib);
                          }),
                        );
                      }
                      return of(null);
                    }),
                  );
                } else {
                  if (cached) {
                    const patch = jiff.diff(cached.document, lib.document, diffOptions);
                    if (patch && patch.length > 0) {
                      return this.documentRestService.patchUserLibraryDocument(lib.uuid, patch).pipe(
                        concatMap(version => {
                          lib.version = version;
                          lib.document.version = version;
                          return this.offline.save(UserLibraryDocument, lib);
                        }),
                      );
                    }
                  }
                  return of(null);
                }
              }),
            );
          },
          deleted: lib => this.documentRestService.deleteUserLibraryDocument(lib.uuid),
        }).pipe(
          concatMap(lib => this.saveUserLibraryCache(lib)),
          map(() => null),
        );
      }),
    );
  }

  /**
   * Pulls any updates to document models from the server.
   */
  protected pull(groupUUIDs?: string[]): Observable<void> {
    this.syncLog.info('Fetching documents from the server and saving to offline database.');

    return forkJoin([this.pullExercisePrograms(), this.pullPlanMetadata(groupUUIDs), this.pullUserLibrary()]).pipe(
      map(() => undefined),
      concatMap(() => {
        return forkJoin([this.offline.getAll<PlanMetadata>(PlanMetadata), this.offline.getAll<PlanDocument>(PlanDocument)]).pipe(
          concatMap((result: [PlanMetadata[], PlanDocument[]]) => {
            const planMetadata = result[0];
            const plans = result[1];
            const plansToPull = intersection(
              planMetadata.map(p => p.uuid),
              plans.map(p => p.uuid),
            );
            const plansToDelete = difference(
              plans.map(p => p.uuid),
              planMetadata.map(p => p.uuid),
            );
            const obs: Observable<any>[] = [];

            if (plansToPull.length > 0) {
              obs.push(this.pullPlans(plansToPull).pipe(concatMap(() => forkJoin(plans.map(plan => this.markDownloaded(plan.uuid))))));
            }

            if (plansToDelete.length > 0) {
              obs.push(...plansToDelete.map(uuid => this.offline.deleteByKey(PlanDocument, uuid)));
            }

            if (obs.length > 0) {
              return forkJoin(obs).pipe(map(() => undefined));
            } else {
              return of(undefined);
            }
          }),
        );
      }),
    );
  }

  /**
   * Pulls all exercise program documents from the server.
   */
  protected pullExercisePrograms() {
    return this.documentRestService.getExercisePrograms().pipe(
      concatMap(uuids => {
        if (uuids.length > 0) {
          return this.getManyDocuments<ExerciseProgramDocument, Program>({
            model: ExerciseProgramDocument,
            uuids,
            clear: true,
            request: uuid => this.documentRestService.getExerciseProgram(uuid),
            diffRequest: record =>
              this.documentRestService.getExerciseProgramDiff(record).pipe(
                map(res => {
                  if (res.patch.length > 0) {
                    record.document = jiff.patch(res.patch, record.document);
                  }

                  record.version = res.version;
                  return record;
                }),
              ),
          });
        } else {
          return of([]);
        }
      }),
      concatMap(program => this.saveExerciseProgramDocumentCache(program)),
    );
  }

  /**
   * Pushes exercise programs to the server
   */
  protected pushExerciseProgram() {
    return this.offline.getAll<ExerciseProgramDocumentCache>(ExerciseProgramDocumentCache).pipe(
      concatMap(cache => {
        return this.syncDirty<ExerciseProgramDocument>(ExerciseProgramDocument, {
          model: ExerciseProgramDocument,
          created: () => of(null),
          updated: program => {
            return this.documentRestService.getExerciseProgramDocumentLatestVersion(program.uuid).pipe(
              concatMap(latestVersion => {
                const cached = cache.find(c => c.uuid === program.uuid);

                if (latestVersion !== program.version) {
                  // if local version does not match remote, construct remote from patch stream and send
                  // diff with local as patch to server
                  return this.documentRestService.getExerciseProgramDiff(program).pipe(
                    concatMap(res => {
                      const remote = jiff.patch(res.patch, cached.document);
                      const patch = jiff.diff(remote, program.document, diffOptions);
                      if (patch && patch.length > 0) {
                        return this.documentRestService.patchExerciseProgramDocument(program.uuid, patch).pipe(
                          concatMap(version => {
                            program.version = version;
                            program.document.version = version;
                            return this.offline.save(ExerciseProgramDocument, program);
                          }),
                        );
                      }
                      return of(null);
                    }),
                  );
                } else {
                  if (cached) {
                    const patch = jiff.diff(cached.document, program.document, diffOptions);
                    if (patch && patch.length > 0) {
                      return this.documentRestService.patchExerciseProgramDocument(program.uuid, patch).pipe(
                        concatMap(version => {
                          program.version = version;
                          program.document.version = version;
                          return this.offline.save(ExerciseProgramDocument, program);
                        }),
                      );
                    }
                  }
                  return of(null);
                }
              }),
            );
          },
          deleted: () => of(null),
        }).pipe(
          concatMap(program => this.saveExerciseProgramDocumentCache(program)),
          map(() => null),
        );
      }),
    );
  }

  /**
   * Pulls all plan metadata from the server
   */
  protected pullPlanMetadata(groupUUIDs?: string[]) {
    if (groupUUIDs && groupUUIDs.length > 0) {
      const requests = groupUUIDs.map(groupUUID => this.documentRestService.getGroupPublishedPlans(groupUUID));
      return this.getMany<PlanMetadata>({
        model: PlanMetadata,
        clear: true,
        request: forkJoin(requests).pipe(
          map(res => {
            const plans: PlanMetadata[] = [];
            res.forEach(publishedPlans => {
              publishedPlans.forEach(plan => {
                if (!plans.some(p => p.uuid === plan.uuid)) {
                  plans.push(plan);
                }
              });
            });

            return plans;
          }),
        ),
      });
    } else {
      return this.getMany<PlanMetadata>({
        model: PlanMetadata,
        clear: true,
        request: this.documentRestService.getPlans(),
      });
    }
  }

  /**
   * Pulls plan documents with provided uuids from the server
   * @param uuids plan uuids
   */
  protected pullPlans(uuids: string[], getLatestVersion?: boolean) {
    return this.getManyDocuments<PlanDocument, Plan>({
      model: PlanDocument,
      uuids,
      request: uuid => this.documentRestService.getPlanDocument(uuid),
      diffRequest: record => {
        if (getLatestVersion) {
          return this.documentRestService.getPlanDocument(record.uuid);
        } else {
          return this.documentRestService.getPlanDocumentDiff(record).pipe(
            tap(res => this.planUpdates.next({ plan: record, patch: res })),
            concatMap(res => {
              record.version = res.version;

              if (res.patch.length > 0) {
                // if there are changes from the server, apply to cache and take diff of local record
                return this.offline.getRecord<PlanDocumentCache>(PlanDocumentCache, record.uuid).pipe(
                  map(cache => {
                    // apply remote changes to local cache
                    const patched = jiff.patch(res.patch, cache.document);
                    // take diff of local document and patched cache
                    const diff = jiff.diff(record.document, patched, diffOptions);
                    if (diff.length > 0) {
                      // if local document is still different from cache, apply diff to local document
                      record.document = jiff.patch(diff, record.document);
                    }

                    return record;
                  }),
                );
              } else {
                return of(record);
              }
            }),
          );
        }
      },
      clear: false,
    }).pipe(
      concatMap(plans => this.saveCache(plans)),
      tap(() => this.updateTotalProgress(uuids.length)),
    );
  }

  protected pullWorkoutSessions() {
    return this.documentRestService.getWorkoutSessionDocumentIds().pipe(
      concatMap(ids => {
        if (ids.length > 0) {
          return this.getManyDocuments<WorkoutSessionDocument, WorkoutSession>({
            model: WorkoutSessionDocument,
            uuids: ids,
            clear: true,
            request: uuid => this.documentRestService.getWorkoutSessionDocument(uuid),
            diffRequest: record => of(record),
          });
        } else {
          return of([]);
        }
      }),
    );
  }

  protected pushWorkoutSessions() {
    return this.syncDirty<WorkoutSessionDocument>(WorkoutSessionDocument, {
      created: record => this.documentRestService.createWorkoutSessionDocument(record),
      updated: () => of(null),
      deleted: () => of(null),
      model: WorkoutSessionDocument,
    });
  }

  /**
   * Pulls a user library document from the server
   */
  protected pullUserLibrary(): Observable<void> {
    return this.documentRestService.getUserLibraryDocumentIds().pipe(
      concatMap(uuids => {
        if (uuids && uuids.length > 0) {
          return this.getManyDocuments<UserLibraryDocument, UserLibrary>({
            model: UserLibraryDocument,
            clear: true,
            uuids,
            request: uuid => this.documentRestService.getUserLibraryDocument(uuid),
            diffRequest: record =>
              this.mergeChanges(
                record,
                this.documentRestService.getUserLibraryDocumentDiff(record),
                this.offline.getRecord<UserLibraryDocumentCache>(UserLibraryDocumentCache, record.uuid),
              ),
          }).pipe(
            concatMap(lib => this.saveUserLibraryCache(lib)),
            map(() => null),
          );
        } else {
          return of(null);
        }
      }),
    );
  }

  private mergeChanges<TRecord extends DocumentRecord<TDocument>, TDocument, TCache extends DocumentRecord<TDocument>>(
    record: TRecord,
    diffRequest: Observable<{ version: number; patch: object[] }>,
    cacheRequest: Observable<TCache>,
  ) {
    return diffRequest.pipe(
      concatMap(res => {
        record.version = res.version;

        if (res.patch.length > 0) {
          // if there are changes from the server, apply to cache and take diff of local record
          return cacheRequest.pipe(
            map(cache => {
              // apply remote changes to local cache
              const patched = jiff.patch(res.patch, cache.document);
              // take diff of local document and patched cache
              const diff = jiff.diff(record.document, patched, diffOptions);
              if (diff.length > 0) {
                // if local document is still different from cache, apply diff to local document
                record.document = jiff.patch(diff, record.document);
              }

              return record;
            }),
          );
        } else {
          return of(record);
        }
      }),
    );
  }

  /**
   * Marks a plan's sync status with the server.
   * @param uuid plan uuid
   */
  protected markDownloaded(uuid: string) {
    return this.offline.getRecord<PlanMetadata>(PlanMetadata, uuid).pipe(
      concatMap(planMetadata => {
        if (planMetadata) {
          planMetadata.isDownloaded = true;
          return this.offline.save(PlanMetadata, planMetadata);
        } else {
          return of(null);
        }
      }),
    );
  }

  protected saveCache(plans: PlanDocument[]) {
    const cache = plans.map(plan => new PlanDocumentCache(plan));
    return this.offline.save(PlanDocumentCache, cache);
  }

  // TODO: make more generic
  protected saveUserLibraryCache(lib: UserLibraryDocument[]) {
    return this.offline.save(
      UserLibraryDocumentCache,
      lib.map(l => new UserLibraryDocumentCache(l)),
    );
  }

  protected saveExerciseProgramDocumentCache(program: ExerciseProgramDocument[]) {
    return this.offline.save(
      ExerciseProgramDocumentCache,
      program.map(p => new ExerciseProgramDocumentCache(p)),
    );
  }

  protected pullObservations() {
    return this.documentRestService.getObservationDocumentIds().pipe(
      concatMap(ids => {
        if (ids.length > 0) {
          return this.getManyDocuments<ObservationDocument, Observation>({
            model: ObservationDocument,
            uuids: ids,
            clear: true,
            request: uuid => this.documentRestService.getObservationDocument(uuid),
            diffRequest: record => of(record),
          });
        } else {
          return of([]);
        }
      }),
    );
  }

  protected pushObservations() {
    return this.syncDirty<ObservationDocument>(ObservationDocument, {
      created: record => this.documentRestService.createObservationDocument(record),
      updated: () => of(null),
      deleted: () => of(null),
      model: ObservationDocument,
    });
  }
}
